import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileCheck2, ShieldCheck } from "lucide-react";
import { api } from "@/app/api";
import { Card } from "@/app/ui";
import { formatDate } from "@/lib/opportunities";

const statusLabel = (value) => (value || "unknown").replaceAll("_", " ");

export default function EligibilityAssessments() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/individual/eligibility-assessments")
      .then((response) => setItems(response.data.assessments))
      .catch(() => setItems([]));
  }, []);

  return (
    <div data-testid="eligibility-assessments-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-ink">Eligibility assessments</h1>
          <p className="mt-2 max-w-2xl text-slatey">Return to saved opportunity assessments and complete official verification when required.</p>
        </div>
        <Link to="/app/discover" data-testid="assessments-discover-link" className="rounded-full bg-cobalt px-4 py-2.5 text-sm font-bold text-white">Discover opportunities</Link>
      </div>

      <div className="mt-6 space-y-4">
        {items?.map((item) => (
          <Card key={item.id} data-testid={`assessment-${item.id}`} className="p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full bg-cobalt/10 px-3 py-1 text-xs font-bold capitalize text-cobalt">{statusLabel(item.status)}</span>
                  {item.verification_status?.includes("required") && <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">Official verification required</span>}
                </div>
                <h2 className="mt-3 font-display text-2xl font-bold text-ink">{item.result?.opportunity?.title}</h2>
                <p className="mt-1 text-sm text-slatey">{item.result?.opportunity?.provider}</p>
                <p className="mt-3 text-sm text-slatey">Assessed {formatDate((item.updated_at || "").slice(0, 10))}</p>
              </div>
              <Link to={`/eligibility/${item.opportunity_slug}?assessment=${item.id}`} data-testid={`continue-assessment-${item.id}`}
                className="inline-flex items-center gap-2 rounded-full bg-cobalt px-4 py-2.5 text-sm font-bold text-white">
                Continue <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Card>
        ))}
      </div>

      {items?.length === 0 && (
        <Card data-testid="assessments-empty" className="mt-6 text-center">
          <FileCheck2 className="mx-auto h-10 w-10 text-cobalt" />
          <h2 className="mt-4 font-display text-2xl font-bold text-ink">No saved assessments yet</h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slatey">Check eligibility for an opportunity to create an indicative OpenDegree assessment.</p>
          <Link to="/app/discover" className="mt-5 inline-flex rounded-full bg-cobalt px-5 py-3 text-sm font-bold text-white">Discover opportunities</Link>
        </Card>
      )}

      <div className="mt-6 flex items-start gap-3 rounded-3xl border border-slate-200 bg-white p-5 text-sm leading-6 text-slatey">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-cobalt" />
        <p>OpenDegree assessments are indicative and auditable. Official verification and provider decisions remain separate.</p>
      </div>
    </div>
  );
}
